import { Helpers } from '../Modules/Helpers.js'

/**
 * Controls the behavior of the Main Navigation, including showing and hiding the submenus
 *
 * @type {DesktopPageNavigation}
 * @class
 */
export class DesktopPageNavigation {
    /**
     * Constructor retrieves all required elements from dom and binds all necessary events
     */
    constructor() {
        // Activate to display the transparent background until the selected scrollY is reached
        this.SCROLL_DISTANCE_FOR_BACKGROUND = 0
        this.lastTapped = null
        this.blockInfiniteCall = false
        this.activeSubmenu = null
        this.header = document.querySelector('.js__header')
        this.backend = document.querySelector('body.neos-backend')
        this.mainNav = document.querySelector('.js__main-nav')
        this.submenuOpeners = document.querySelectorAll('.nav-level-1 > .js__main-nav__sub-opener')
        this.submenuClosers = document.querySelectorAll('.nav-level-1 > .main-nav__sub > .js__main-nav__sub-closer')
        this.submenus = document.querySelectorAll('.nav-level-1 > .js__main-nav__sub')
        //flyout behaviours
        this.heightOfSubmenus = 0
        this.anySubIsOpen = false
        this.NAVIGATION_STATE_VISIBLE = 'header--menu-visible'
        this.NAVIGATION_STATE_ANIMATE = 'header--menu-animate'
        this.showDelay = 300 // ms like the underline animation from css
        this.showTimer = null

        if (this.isFrontend()) {
            // bind click and touch event listeners
            if (this.isTouch()) {
                this.addTapEvents()
            } else {
                this.addMouseEvents()
            }

            // other events
            this.bindHideOnScroll()
            this.bindHideOnClickOutsideSubmenu()
            this.bindBackgroundAfterScrollDistance()
            this.addResizeEvents()

            // if its not on mobile while constructing hide Submenus
            // because for mobile menu the current submenus are preopened
            // this would cause an initial open menu on desktop too
            // todo TDWEBSEITE-254 Refactoring to prevent initial dom manipulation
            if (!Helpers.isOnMobile()) {
                this.determineHeightOfSubmenus()
                this.hideSubmenus()
                // after closing subs, remove initial closed class
                this.mainNav.classList.remove('main-nav__initial-closed')
            }
        }
    }

    /**
     * Touch/Tab Events
     */
    addTapEvents() {
        this.submenuOpeners.forEach((element) =>
            this.addListenerMulti(element, 'click touchstart MSPointerDown', (e) => this.isDoubleTapped(e))
        )
        this.submenuClosers.forEach((element) =>
            this.addListenerMulti(element, 'click touchstart MSPointerDown', () => this.closeSubmenus())
        )
    }

    /**
     * Click/Hover Events
     */
    addMouseEvents() {
        //hover open
        this.submenuOpeners.forEach((submenuOpener) =>
            submenuOpener.addEventListener('mouseenter', (e) => this.delayedOpener(e))
        )
        this.submenuOpeners.forEach((submenuOpener) =>
            submenuOpener.addEventListener('mouseleave', () => this.delayedCanceler())
        )
        //click redirect/nativeEvent
        this.submenuOpeners.forEach((submenuOpener) =>
            submenuOpener.addEventListener('click', (e) => this.callNativeClickEvent(e))
        )
        //click close submenu
        this.submenuClosers.forEach((submenuCloser) =>
            submenuCloser.addEventListener('click', () => this.closeSubmenus())
        )
    }

    /**
     * Resize Events
     */
    addResizeEvents() {
        window.addEventListener('resize', () => this.handleHeightOfSubmenus())
    }

    /**
     * Delayed submenu opener by hover, mouseover
     *
     * @param {Event} e - The event of the selected menu item to open the submenu
     *
     * @returns {undefined}
     */
    delayedOpener(e) {
        let event = e
        let _this = this
        this.menuEnterTimer = setTimeout(function () {
            _this.showSubmenu(event)
        }, this.showDelay)
    }

    delayedCanceler() {
        clearTimeout(this.menuEnterTimer)
    }

    /**
     * used to figure out the current height of the highest submenu
     */
    determineHeightOfSubmenus() {
        let beforeResizeHeight = this.heightOfSubmenus
        this.submenus.forEach((sub) => {
            if (sub.offsetHeight > this.heightOfSubmenus) {
                this.heightOfSubmenus = sub.offsetHeight
            }
        })
        if (beforeResizeHeight !== this.heightOfSubmenus) this.setHeightOfSubmenus(this.heightOfSubmenus + 'px')
    }

    /**
     * used to provide same heights of submenus to prevent clipping issues
     */
    setHeightOfSubmenus(value) {
        this.submenus.forEach((sub) => {
            sub.style.height = value
        })
    }

    /**
     * set height of menus for mobile to auto and determine if
     */
    handleHeightOfSubmenus() {
        if (Helpers.isOnMobile()) {
            this.setHeightOfSubmenus('auto')
        } else if (this.heightOfSubmenus === 0) {
            this.determineHeightOfSubmenus()
        }
    }

    /**
     * Show a submenu based on a menu item
     *
     * @param {Event} clickedSubOpenerEvent - The event of the clicked menu item to open the submenu
     *
     * @returns {undefined}
     */
    showSubmenu(clickedSubOpenerEvent) {
        if (Helpers.isOnMobile()) {
            return
        }

        // is any submenu opened before
        if (this.activeSubmenu) {
            this.header.classList.remove(this.NAVIGATION_STATE_ANIMATE)
        } else {
            this.header.classList.add(this.NAVIGATION_STATE_ANIMATE)
        }

        // hide the previously opened submenu
        this.hideSubmenus()
        let clickedSubOpener = clickedSubOpenerEvent.target
        clickedSubOpener.classList.add('main-nav__sub-opener--sub-open')

        let desiredSubmenu = clickedSubOpener.parentNode.querySelector('.js__main-nav__sub')
        desiredSubmenu.classList.add('main-nav__sub--visible')

        this.header.classList.add(this.NAVIGATION_STATE_VISIBLE)
        this.activeSubmenu = desiredSubmenu

        // stop propagation to prevent the detection if the click was outside of the submenu which would result in
        // immediately hiding the submenu again
        if (this.isTouch()) {
            clickedSubOpenerEvent.stopPropagation()
            clickedSubOpenerEvent.preventDefault()
        }
    }

    /**
     * used as proxy to invoke an css animation on closing the navigation
     */
    closeSubmenus() {
        if (this.activeSubmenu) {
            this.header.classList.add(this.NAVIGATION_STATE_ANIMATE)
        }
        this.hideSubmenus()
    }

    /**
     * Hide all open submenus
     *
     * @returns {undefined}
     */
    hideSubmenus() {
        this.activeSubmenu = null
        this.lastTapped = null
        this.submenus.forEach((submenu) => {
            submenu.classList.remove('main-nav__sub--visible')
        })
        this.submenuOpeners.forEach((submenuOpener) => {
            submenuOpener.classList.remove('main-nav__sub-opener--sub-open')
        })
        this.header.classList.remove(this.NAVIGATION_STATE_VISIBLE)
    }

    /**
     * Bind the scrollDown event to hide the submenu
     *
     * @returns {undefined}
     */
    bindHideOnScroll() {
        window.addEventListener('scrollDown', () => {
            if (this.activeSubmenu) {
                this.closeSubmenus()
            }
        })
    }

    /**
     * Bind the scrollDown event to hide the submenu
     *
     * @returns {undefined}
     */
    bindBackgroundAfterScrollDistance() {
        window.addEventListener('scroll', () => {
            if (window.pageYOffset > this.SCROLL_DISTANCE_FOR_BACKGROUND) {
                this.mainNav.classList.add('main-nav__list-wrapper--has-background')
            } else {
                this.mainNav.classList.remove('main-nav__list-wrapper--has-background')
            }
        })
    }

    /**
     * Bind click event to check weither the click was outside the submenu, which results in hiding the submenu
     *
     * @returns {undefined}
     */
    bindHideOnClickOutsideSubmenu() {
        document.addEventListener('click', (e) => {
            if (this.activeSubmenu && !this.activeSubmenu.contains(e.target)) {
                this.closeSubmenus()
            }
        })
    }

    /**
     * Add one or more listeners to an element
     * @param {Node} element - DOM element to add listeners to
     * @param {string} eventNames - space separated list of event names, e.g. 'click change'
     * @param {Function} listener - function to attach for each event as a listener
     */
    addListenerMulti(element, eventNames, listener) {
        let events = eventNames.split(' ')
        for (let i = 0, iLen = events.length; i < iLen; i++) {
            element.addEventListener(events[i], listener, false)
        }
    }

    /**
     * handle the tapped element and open submenu or redirect to nativeEvent
     * @param e {Event}
     */
    isDoubleTapped(e) {
        const { target } = e

        // if current is not last open Submenu
        if (target != this.lastTapped) {
            this.showSubmenu(e)
            this.lastTapped = target
        } else {
            this.callNativeClickEvent(e)
        }
    }

    /**
     * call native Event
     * @param e {Event}
     */
    callNativeClickEvent(e) {
        const { target, nativeEvent } = e
        // prevent infinite calls
        if (this.blockInfiniteCall === false) {
            this.blockInfiniteCall = true
            // trigger native event of element
            let clonedNativeEvent = new MouseEvent('click', nativeEvent)
            target.dispatchEvent(clonedNativeEvent)
            // enable for next non-page-changing events
            this.blockInfiniteCall = false
        }
    }

    /**
     * is touch available
     * @returns {boolean}
     */
    isTouch() {
        return !(
            !('ontouchstart' in window) &&
            !navigator.msMaxTouchPoints &&
            !navigator.userAgent.toLowerCase().match(/windows phone os 7/i)
        )
    }

    /**
     * is backend context
     * @returns {boolean}
     */
    isFrontend() {
        return this.backend == null
    }
}
